import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Chip,
  LinearProgress,
  Button,
  Avatar,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
  ArrowBack as ArrowBackIcon,
  Transform as TransformIcon,
} from '@mui/icons-material';
import { getDealById, getDealFinancials, getDealQuestions, getDealFlags, dealsApi, getDealDocuments, getFunds, getOrganizationUsers, sharedDealsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { DealStatus } from '../types';
import DocumentUpload from '../components/DocumentUpload';
import { MetricCardSkeleton } from '../components/LoadingSkeleton';
import { Card } from '../components/Card';
import { MarkdownRenderer } from '../components/Chat';
import { SharingTab } from '../components/SharingTab';

// Interfaces from DealDetailsModal
interface FinancialData {
  revenue: {
    current?: number;
    growth_yoy?: number;
    historical?: { [year: string]: number };
  };
  ebitda: {
    current?: number;
    margin?: number;
    historical?: { [year: string]: { value: number; margin: number } };
  };
  cash_flow: {
    operating?: number;
    investing?: number;
    financing?: number;
    free?: number;
  };
  balance_sheet: {
    assets?: number;
    liabilities?: number;
    equity?: number;
    working_capital?: number;
  };
  growth_metrics: {
    yoy_growth?: number;
    three_year_cagr?: number;
    rule_of_40?: number;
  };
  customer_metrics: {
    concentration?: number;
    churn_rate?: number;
    retention_rate?: number;
  };
  kpis: {
    recurring_revenue_percentage?: number;
    gross_profit_margin?: number;
    cac?: number;
    ltv?: number;
    ltv_cac_ratio?: number;
    payback_period_months?: number;
  };
  key_personnel?: Array<{
    name: string;
    role: string;
  }>;
}

interface Flag {
  id: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  status: 'unresolved' | 'resolved';
  source_section?: string;
}

interface Question {
  id: string;
  question: string;
  category: string;
  priority: 'low' | 'medium' | 'high';
  status: 'draft' | 'sent' | 'answered';
  answer?: string;
  assigned_to?: string;
  answered_by?: string;
  answered_at?: string;
  due_date?: string;
  created_at: string;
  sub_questions?: string[];
  rationale?: string;
  expected_data_sources?: string[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`deal-detail-tabpanel-${index}`}
      aria-labelledby={`deal-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `deal-detail-tab-${index}`,
    'aria-controls': `deal-detail-tabpanel-${index}`,
  };
}

const DealSourcingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, currentOrganization } = useAuth();
  const [deal, setDeal] = useState<any>(null);
  const [aiScore, setAiScore] = useState<any>(null);
  const [financials, setFinancials] = useState<FinancialData | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [scoringLoading, setScoringLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isScoringInProgress, setIsScoringInProgress] = useState(false);
  const [scoringError, setScoringError] = useState<string | null>(null);
  const [funds, setFunds] = useState<any[]>([]);
  const [selectedFund, setSelectedFund] = useState<string>('auto');
  const [dealDocuments, setDealDocuments] = useState<any[]>([]);
  const [fundThesisModalOpen, setFundThesisModalOpen] = useState(false);
  const [selectedFundThesis, setSelectedFundThesis] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [convertingToProject, setConvertingToProject] = useState(false);
  // const [answeringQuestion, setAnsweringQuestion] = useState<string | null>(null);
  // const [answeringAll, setAnsweringAll] = useState(false);
  const [showScoringPrompt, setShowScoringPrompt] = useState(false);
  const [scoringPromptFund, setScoringPromptFund] = useState<string>('auto');
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const scoringPollingRef = useRef<NodeJS.Timeout | null>(null);
const handleOpenUploadModal = () => setIsUploadModalOpen(true);
const handleCloseUploadModal = () => setIsUploadModalOpen(false);


  const handleUploadInitiated = (documentId: string) => {
    setIsProcessing(true);
    pollDocumentStatus(documentId);
  };

  const loadDealDetails = useCallback(async () => {
    if (!id) return;
    try {
      const [fetchedDeal, fetchedAiScore, fetchedFinancials, fetchedFlags, fetchedQuestions] = await Promise.all([
        getDealById(id),
        dealsApi.getDealScore(id).catch(() => null),
        getDealFinancials(id).catch(() => null),
        getDealFlags(id).catch(() => []),
        getDealQuestions(id).catch(() => []),
      ]);
      setDeal(fetchedDeal);
      setAiScore(fetchedAiScore);
      setFinancials(fetchedFinancials);
      setFlags(fetchedFlags);
      setQuestions(fetchedQuestions);
    } catch (error) {
      console.error('Error fetching deal details:', error);
    }
  }, [id]);

  const pollDocumentStatus = useCallback((documentId: string) => {
    if (!id) return;
    pollingRef.current = setInterval(async () => {
      try {
        // Instead of polling individual document, poll all deal documents
        const response = await getDealDocuments(id);
        if (response && response.length > 0) {
          const targetDoc = response.find((doc: any) => doc.id === documentId || doc.document_id === documentId);
          if (targetDoc) {
            if (targetDoc.processing_status === 'completed') {
              setIsProcessing(false);
              if (pollingRef.current) clearInterval(pollingRef.current);
              // Reload deal details after processing is complete
              const [fetchedAiScore, fetchedFinancials, fetchedFlags, fetchedQuestions] = await Promise.all([
                dealsApi.getDealScore(id).catch(() => null),
                getDealFinancials(id).catch(() => null),
                getDealFlags(id).catch(() => []),
                getDealQuestions(id).catch(() => []),
              ]);
              setAiScore(fetchedAiScore);
              setFinancials(fetchedFinancials);
              setFlags(fetchedFlags);
              setQuestions(fetchedQuestions);
              
              // Show scoring prompt if no score exists yet
              if (!fetchedAiScore || typeof fetchedAiScore.score === 'undefined') {
                setShowScoringPrompt(true);
              }
            } else if (targetDoc.processing_status === 'failed') {
              setIsProcessing(false);
              if (pollingRef.current) clearInterval(pollingRef.current);
            }
          } else {
            // Document not found in list, stop polling
            setIsProcessing(false);
            if (pollingRef.current) clearInterval(pollingRef.current);
          }
        } else {
          // No documents found, stop polling
          setIsProcessing(false);
          if (pollingRef.current) clearInterval(pollingRef.current);
        }
      } catch (error) {
        console.error('Error polling document status:', error);
        setIsProcessing(false);
        if (pollingRef.current) clearInterval(pollingRef.current);
      }
    }, 5000); // Poll every 5 seconds like in CreateDealDialog
  }, [id]);

  useEffect(() => {
    const initialLoad = async () => {
      if (!id) return;
      setLoading(true);
      
      try {
        // Load deal first - this is the minimum required to show the page
        const fetchedDeal = await getDealById(id);
        setDeal(fetchedDeal);
        setLoading(false); // Stop loading immediately after deal is loaded
        
        // Load funds and users in parallel (non-blocking)
        Promise.all([
          getFunds().then(fundsData => setFunds(fundsData.funds || [])).catch(err => console.error('Failed to fetch funds:', err)),
          (async () => {
            try {
              const organization = localStorage.getItem('organization');
              if (organization) {
                const org = JSON.parse(organization);
                const usersData = await getOrganizationUsers(org.id);
                setUsers(usersData.members || []);
              }
            } catch (error) {
              console.error('Failed to fetch users:', error);
            }
          })()
        ]);

        // Load documents
        const response = await getDealDocuments(id);
        setDealDocuments(response || []);
        
        // Check if any document is processing
        if (response && response.length > 0) {
          const processingDoc = response.find((doc: any) => doc.processing_status === 'processing');
          if (processingDoc) {
            setIsProcessing(true);
            pollDocumentStatus(processingDoc.id);
          }
        }
        
        // Load other data independently (non-blocking)
        dealsApi.getDealScore(id).then(setAiScore).catch(() => setAiScore(null));
        getDealFinancials(id).then(setFinancials).catch(() => setFinancials(null));
        getDealFlags(id).then(setFlags).catch(() => setFlags([]));
        getDealQuestions(id).then(setQuestions).catch(() => setQuestions([]));
        
      } catch (error) {
        console.error('Error during initial load:', error);
        setDeal(null);
        setLoading(false);
      }
    };

    initialLoad();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (scoringPollingRef.current) {
        clearInterval(scoringPollingRef.current);
      }
    };
  }, [id, pollDocumentStatus]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Helper functions from DealDetailsModal
  const formatCurrency = (value: number | undefined, suffix = '') => {
    if (value === undefined || value === null) return 'N/A';
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M${suffix}`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K${suffix}`;
    }
    return `$${value.toFixed(0)}${suffix}`;
  };

  const formatPercentage = (value: number | undefined) => {
    if (value === undefined || value === null) return 'N/A';
    return `${value.toFixed(1)}%`;
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };
  
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'answered': return 'success';
      case 'sent': return 'info';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  // Helper function to get fund name from fund ID
  const getFundName = (fundId: string) => {
    const fund = funds.find(f => f.id === fundId);
    return fund ? fund.name : 'Unknown Fund';
  };

  // Helper function to get user name from user ID
  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId || u.user_id === userId);
    return user ? user.user_name || user.user_email || 'Unknown User' : 'Unknown User';
  };

  // Handle viewing fund thesis
  const handleViewFundThesis = async (fundId: string) => {
    try {
      // For now, we'll create a mock thesis object since we don't have a specific API endpoint
      // In a real implementation, you'd fetch the thesis details from the API
      const fund = funds.find(f => f.id === fundId);
      if (fund) {
        setSelectedFundThesis({
          fund_name: fund.name,
          fund_id: fundId,
          // Mock thesis data - in reality this would come from an API call
          name: `${fund.name} Investment Thesis`,
          description: `Investment thesis and strategy for ${fund.name}`,
          core_investment_themes: ['Technology Innovation', 'Market Leadership', 'Scalable Business Models'],
          value_creation_levers: ['Operational Excellence', 'Strategic Partnerships', 'Market Expansion'],
          sector_focus: ['Technology', 'Healthcare', 'Financial Services'],
          deal_breakers: ['Regulatory Issues', 'Poor Management', 'Limited Market Size'],
          risk_appetite: 'Moderate to High',
          vintage: fund.vintage,
          total_commitments: fund.total_commitments,
          status: fund.status
        });
        setFundThesisModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching fund thesis:', error);
    }
  };

  const handleCloseFundThesisModal = () => {
    setFundThesisModalOpen(false);
    setSelectedFundThesis(null);
  };

  // Handle converting deal to project
  // const handleAnswerQuestion = async (questionId: string) => {
  //   if (!id) return;
  //   try {
  //     setAnsweringQuestion(questionId);
  //     const updatedQuestion = await generateQuestionAnswer(id, questionId);
      
  //     // Update questions state
  //     setQuestions(prev => prev.map(q => 
  //       q.id === questionId ? { ...q, ...updatedQuestion } : q
  //     ));
  //   } catch (error) {
  //     console.error('Error generating answer:', error);
  //   } finally {
  //     setAnsweringQuestion(null);
  //   }
  // };

  // const handleAnswerAllQuestions = async () => {
  //   if (!id) return;
  //   try {
  //     setAnsweringAll(true);
  //     const result = await generateAllAnswers(id);
      
  //     // Reload questions to get updated answers
  //     const fetchedQuestions = await getDealQuestions(id);
  //     setQuestions(fetchedQuestions);
  //   } catch (error) {
  //     console.error('Error generating all answers:', error);
  //   } finally {
  //     setAnsweringAll(false);
  //   }
  // };

  const handleConvertToProject = async () => {
    if (!id || !deal) return;
    
    try {
      setConvertingToProject(true);
      
      const token = localStorage.getItem('token');
      const organization = localStorage.getItem('organization');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      if (organization) {
        const org = JSON.parse(organization);
        headers['X-Organization-ID'] = org.id;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/deals/${id}/convert-to-project`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          include_documents: true,
          include_financials: true,
          include_scoring: true
        }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Navigate to the new project
        navigate(`/projects/${result.project_id}`);
      } else {
        throw new Error(`Conversion failed: ${response.status}`);
      }
    } catch (err) {
      console.error('Error converting deal to project:', err);
      // You could add a toast notification here
    } finally {
      setConvertingToProject(false);
    }
  };

  const pollScoringStatus = useCallback(() => {
    if (!id) return;
    let attempts = 0;
    const maxAttempts = 24; // 2 minutes max (5 second intervals)
    
    scoringPollingRef.current = setInterval(async () => {
      attempts++;
      try {
        const scoreResponse = await dealsApi.getDealScore(id);
        if (scoreResponse && !('message' in scoreResponse) && typeof scoreResponse.score !== 'undefined') {
          // Scoring completed successfully
          setAiScore(scoreResponse);
          setIsScoringInProgress(false);
          setScoringLoading(false);
          setScoringError(null);
          if (scoringPollingRef.current) clearInterval(scoringPollingRef.current);
        } else if (attempts >= maxAttempts) {
          // Timeout - scoring may have failed
          setIsScoringInProgress(false);
          setScoringLoading(false);
          setScoringError('Scoring timed out. The process may have failed or is taking longer than expected.');
          if (scoringPollingRef.current) clearInterval(scoringPollingRef.current);
        }
      } catch (error) {
        console.error('Error polling scoring status:', error);
        if (attempts >= maxAttempts) {
          // Stop polling after max attempts and show error
          setIsScoringInProgress(false);
          setScoringLoading(false);
          setScoringError('Failed to complete scoring. Please try again.');
          if (scoringPollingRef.current) clearInterval(scoringPollingRef.current);
        }
        // Continue polling if under max attempts
      }
    }, 5000); // Poll every 5 seconds
  }, [id]);

  const handleScoreDeal = async () => {
    if (!id) return;
    try {
      setScoringLoading(true);
      setIsScoringInProgress(true);
      setScoringError(null);
      
      // Call the scoring API with the selected fund
      const token = localStorage.getItem('token');
      const organization = localStorage.getItem('organization');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      if (organization) {
        const org = JSON.parse(organization);
        headers['X-Organization-ID'] = org.id;
      }

      const body: any = {
        force_rescore: false,
        change_reason: 'Manual scoring request from deal detail page'
      };

      // Only include fund_id if a specific fund is selected (not 'auto')
      if (selectedFund !== 'auto' && selectedFund !== '') {
        body.fund_id = selectedFund;
      }
      // When 'auto' is selected, we don't send fund_id at all, letting the backend choose automatically

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/deal-scoring/${id}/score`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Check if scoring completed immediately (rescore case)
        if (result && typeof result.score !== 'undefined') {
          setAiScore(result);
          setIsScoringInProgress(false);
          setScoringLoading(false);
        } else {
          // Scoring is running in background, start polling
          pollScoringStatus();
        }
      } else {
        throw new Error(`Scoring failed: ${response.status}`);
      }
    } catch (err) {
      console.error('Error scoring deal:', err);
      setIsScoringInProgress(false);
      setScoringLoading(false);
      setScoringError(err instanceof Error ? err.message : 'Failed to start scoring. Please try again.');
    }
  };

  const getRecommendationColor = (recommendation: string) => {
    const colors: { [key: string]: 'success' | 'info' | 'warning' | 'error' | 'default' } = {
      advance: 'success',
      evaluate: 'info',
      monitor: 'warning',
      reject: 'error'
    };
    return colors[recommendation] || 'default';
  };

  const renderScoreStars = (score: number) => {
    const stars = [];
    const fullStars = Math.floor(score / 2);
    const hasHalfStar = score % 2 >= 1;
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<StarIcon key={i} fontSize="small" color="primary" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<StarIcon key={i} fontSize="small" color="primary" sx={{ opacity: 0.5 }} />);
      } else {
        stars.push(<StarBorderIcon key={i} fontSize="small" color="action" />);
      }
    }
    return stars;
  };

  const renderNoFinancialData = (title: string) => (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <UploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a CIM document to extract financial information using AI
      </Typography>
      <Button
        variant="contained"
        startIcon={<UploadIcon />}
        onClick={handleOpenUploadModal}
      >
        Upload CIM Document
      </Button>
    </Box>
  );

  // Render functions for tabs
  const renderScoringTab = () => {
    // Show processing overlay if document is being processed
    if (isProcessing) {
      return renderProcessingOverlay();
    }
    
    // Show upload prompt if no documents
    if (dealDocuments.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <UploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Documents Available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please upload a document to continue scoring the deal
          </Typography>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={handleOpenUploadModal}
          >
            Upload Document
          </Button>
        </Box>
      );
    }
    
    // Show AI scoring progress
    if (isScoringInProgress) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>AI Scoring in Progress</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Our AI is analyzing the deal against your investment criteria and generating a comprehensive score...
          </Typography>
          <Alert severity="info" sx={{ mt: 2, maxWidth: 500 }}>
            <Typography variant="body2">
              <strong>What's happening:</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              • Analyzing financial performance and metrics<br/>
              • Evaluating strategic fit with investment thesis<br/>
              • Assessing market opportunity and competitive position<br/>
              • Identifying key strengths and concerns<br/>
              • Generating investment recommendation
            </Typography>
          </Alert>
        </Box>
      );
    }
    
    return (
      <Box>
        {/* Show scoring error if present */}
        {scoringError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Scoring Failed:</strong> {scoringError}
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleScoreDeal}
              sx={{ mt: 1 }}
              disabled={scoringLoading || isScoringInProgress}
            >
              Try Again
            </Button>
          </Alert>
        )}
        
        {aiScore && typeof aiScore.score !== 'undefined' ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
            <Card>
              <Card.Content sx={{ textAlign: 'center' }}>
                <Typography variant="h2" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {aiScore.score.toFixed(1)}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  out of 10
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  {renderScoreStars(aiScore.score)}
                </Box>
                <Chip
                  label={aiScore.recommendation.replace('_', ' ').toUpperCase()}
                  color={getRecommendationColor(aiScore.recommendation)}
                  size="medium"
                  sx={{ fontWeight: 'bold', mb: 2 }}
                />
                {/* Force Rescore button - commented out for production */}
                {/* <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={scoringLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={async () => {
                      if (!id) return;
                      try {
                        setScoringLoading(true);
                        setIsScoringInProgress(true);
                        setScoringError(null);
                        
                        const token = localStorage.getItem('token');
                        const organization = localStorage.getItem('organization');
                        const headers: Record<string, string> = {
                          'Content-Type': 'application/json',
                        };
                        
                        if (token) {
                          headers['Authorization'] = `Bearer ${token}`;
                        }
                        if (organization) {
                          const org = JSON.parse(organization);
                          headers['X-Organization-ID'] = org.id;
                        }

                        const body: any = {
                          force_rescore: true,
                          change_reason: 'Force rescore for testing - requested from deal detail page'
                        };

                        if (selectedFund !== 'auto' && selectedFund !== '') {
                          body.fund_id = selectedFund;
                        }

                        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/deal-scoring/${id}/score`, {
                          method: 'POST',
                          headers,
                          body: JSON.stringify(body),
                        });

                        if (response.ok) {
                          const result = await response.json();
                          
                          if (result && typeof result.score !== 'undefined') {
                            setAiScore(result);
                            setIsScoringInProgress(false);
                            setScoringLoading(false);
                          } else {
                            pollScoringStatus();
                          }
                        } else {
                          throw new Error(`Force rescore failed: ${response.status}`);
                        }
                      } catch (err) {
                        console.error('Error force rescoring deal:', err);
                        setIsScoringInProgress(false);
                        setScoringLoading(false);
                        setScoringError(err instanceof Error ? err.message : 'Failed to force rescore. Please try again.');
                      }
                    }}
                    disabled={scoringLoading || isScoringInProgress}
                  >
                    {scoringLoading ? 'Rescoring...' : 'Force Rescore'}
                  </Button>
                </Box> */}
              </Card.Content>
            </Card>
          </Box>
          
          {/* Score Breakdown - Always show if we have component scores */}
          <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
            <Card>
              <Card.Content>
                <Typography variant="h6" gutterBottom>Score Breakdown</Typography>
                {/* Use breakdown object or individual score fields from LLM response */}
                {[
                  { 
                    key: 'financial_score', 
                    label: 'Financial Performance', 
                    value: aiScore.breakdown?.financial_score || aiScore.financial_score,
                    tooltip: aiScore.breakdown?.financial_explanation || 'Revenue growth, profitability, cash flow generation, and overall financial health'
                  },
                  { 
                    key: 'strategic_fit_score', 
                    label: 'Strategic Fit', 
                    value: aiScore.breakdown?.strategic_fit_score || aiScore.strategic_fit_score,
                    tooltip: aiScore.breakdown?.strategic_fit_explanation || 'Alignment with investment thesis, sector focus, and value creation opportunities'
                  },
                  { 
                    key: 'market_score', 
                    label: 'Market Opportunity', 
                    value: aiScore.breakdown?.market_score || aiScore.market_score,
                    tooltip: aiScore.breakdown?.market_explanation || 'Market size, growth potential, competitive positioning, and barriers to entry'
                  },
                  { 
                    key: 'team_score', 
                    label: 'Management Team', 
                    value: aiScore.breakdown?.team_score || aiScore.team_score,
                    tooltip: aiScore.breakdown?.team_explanation || 'Leadership experience, track record, organizational depth, and execution capability'
                  },
                  { 
                    key: 'risk_score', 
                    label: 'Risk Assessment', 
                    value: aiScore.breakdown?.risk_score || aiScore.risk_score,
                    tooltip: aiScore.breakdown?.risk_explanation || 'Customer concentration, regulatory risks, competitive threats, and operational challenges'
                  }
                ].map(({ key, label, value, tooltip }) => {
                  const score = value ? parseFloat(value.toString()) : 0;
                  return (
                    <Box key={key} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Tooltip title={tooltip} arrow placement="top">
                          <Typography variant="body2" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                            {label}
                          </Typography>
                        </Tooltip>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{score.toFixed(1)}/10</Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={(score / 10) * 100} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          backgroundColor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: score >= 7 ? 'success.main' : score >= 5 ? 'warning.main' : 'error.main'
                          }
                        }} 
                      />
                    </Box>
                  );
                })}
              </Card.Content>
            </Card>
          </Box>

          {/* AI Analysis Explanation */}
          {aiScore.explanation && (
            <Box sx={{ flex: '1 1 100%', minWidth: 300 }}>
              <Card>
                <Card.Content>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PsychologyIcon color="primary" />
                    AI Investment Analysis
                  </Typography>
                  <MarkdownRenderer content={aiScore.explanation} />
                </Card.Content>
              </Card>
            </Box>
          )}
          {aiScore.key_strengths && (
            <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
              <Card>
                <Card.Content>
                  <Typography variant="h6" sx={{ color: 'success.main', mb: 2 }}>✓ Key Strengths</Typography>
                  {aiScore.key_strengths.map((strength: string, index: number) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', mr: 1, mt: 0.5, fontSize: 16 }} />
                      <Typography variant="body2">{strength}</Typography>
                    </Box>
                  ))}
                </Card.Content>
              </Card>
            </Box>
          )}
          {aiScore.key_concerns && (
            <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
              <Card>
                <Card.Content>
                  <Typography variant="h6" sx={{ color: 'warning.main', mb: 2 }}>⚠ Key Concerns</Typography>
                  {aiScore.key_concerns.map((concern: string, index: number) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: 'warning.main', mr: 1 }}>•</Typography>
                      <Typography variant="body2">{concern}</Typography>
                    </Box>
                  ))}
                </Card.Content>
              </Card>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <PsychologyIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>AI Investment Score</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            Get AI-powered investment insights and recommendations by selecting a fund to score against.
          </Typography>
          
          {/* Fund Selection */}
          <Box sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Fund for Scoring</InputLabel>
              <Select
                value={selectedFund}
                onChange={(e) => setSelectedFund(e.target.value)}
                label="Select Fund for Scoring"
              >
                <MenuItem value="auto">
                  <Box>
                    <Typography variant="body2" fontWeight="medium">Auto (Recommended)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Score against best matching fund based on industry and criteria
                    </Typography>
                  </Box>
                </MenuItem>
                {funds.map((fund: any) => (
                  <MenuItem key={fund.id} value={fund.id}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">{fund.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fund.vintage} • ${fund.total_commitments?.toLocaleString()}M • {fund.status}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {selectedFund !== 'auto' && (
              <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                <Typography variant="body2">
                  The deal will be scored specifically against the selected fund's investment thesis and criteria.
                </Typography>
              </Alert>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={scoringLoading ? <CircularProgress size={20} /> : <PsychologyIcon />}
              onClick={handleScoreDeal}
              disabled={scoringLoading || selectedFund === ''}
            >
              {scoringLoading ? 'Analyzing Deal...' : 'Score This Deal'}
            </Button>
            
            {/* Force Rescore button - commented out for production */}
            {/* <Button
              variant="outlined"
              size="large"
              startIcon={scoringLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={async () => {
                if (!id) return;
                try {
                  setScoringLoading(true);
                  setIsScoringInProgress(true);
                  setScoringError(null);
                  
                  const token = localStorage.getItem('token');
                  const organization = localStorage.getItem('organization');
                  const headers: Record<string, string> = {
                    'Content-Type': 'application/json',
                  };
                  
                  if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                  }
                  if (organization) {
                    const org = JSON.parse(organization);
                    headers['X-Organization-ID'] = org.id;
                  }

                  const body: any = {
                    force_rescore: true,
                    change_reason: 'Force rescore for testing - requested from deal detail page'
                  };

                  if (selectedFund !== 'auto' && selectedFund !== '') {
                    body.fund_id = selectedFund;
                  }

                  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/deal-scoring/${id}/score`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(body),
                  });

                  if (response.ok) {
                    const result = await response.json();
                    
                    if (result && typeof result.score !== 'undefined') {
                      setAiScore(result);
                      setIsScoringInProgress(false);
                      setScoringLoading(false);
                    } else {
                      pollScoringStatus();
                    }
                  } else {
                    throw new Error(`Force rescore failed: ${response.status}`);
                  }
                } catch (err) {
                  console.error('Error force rescoring deal:', err);
                  setIsScoringInProgress(false);
                  setScoringLoading(false);
                  setScoringError(err instanceof Error ? err.message : 'Failed to force rescore. Please try again.');
                }
              }}
              disabled={scoringLoading || selectedFund === ''}
            >
              {scoringLoading ? 'Rescoring...' : 'Force Rescore'}
            </Button> */}
          </Box>
        </Box>
      )}
    </Box>
    );
  };

  const renderFinancialsTab = () => {
    if (isProcessing) return renderProcessingOverlay();
    return (
      <Box>
        {!financials ? (
          renderNoFinancialData('No Financial Data Available')
        ) : (
          <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <Card.Content>
                <Typography variant="h6" gutterBottom>Revenue</Typography>
                <Typography variant="h4" color="primary">{formatCurrency(financials.revenue.current)}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="success.main">
                    +{formatPercentage(financials.revenue.growth_yoy)} YoY
                  </Typography>
                </Box>
              </Card.Content>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <Card.Content>
                <Typography variant="h6" gutterBottom>EBITDA</Typography>
                <Typography variant="h4" color="primary">{formatCurrency(financials.ebitda.current)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Margin: {formatPercentage(financials.ebitda.margin)}
                </Typography>
              </Card.Content>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <Card.Content>
                <Typography variant="h6" gutterBottom>Growth Metrics</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">YoY Growth</Typography>
                    <Typography variant="h6">{formatPercentage(financials.growth_metrics.yoy_growth)}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">3-Year CAGR</Typography>
                    <Typography variant="h6">{formatPercentage(financials.growth_metrics.three_year_cagr)}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">Rule of 40</Typography>
                    <Typography variant="h6">{financials.growth_metrics.rule_of_40?.toFixed(1) || 'N/A'}</Typography>
                  </Box>
                </Box>
              </Card.Content>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
    );
  };

  const renderCashFlowTab = () => {
    if (isProcessing) return renderProcessingOverlay();
    return (
      <Box>
        {!financials ? (
          renderNoFinancialData('No Cash Flow Data Available')
        ) : (
          <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <Card.Content>
                <Typography variant="h6" gutterBottom>Cash Flow</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Operating</Typography>
                  <Typography>{formatCurrency(financials.cash_flow.operating)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Investing</Typography>
                  <Typography>{formatCurrency(financials.cash_flow.investing)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Financing</Typography>
                  <Typography>{formatCurrency(financials.cash_flow.financing)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Free Cash Flow</Typography>
                  <Typography variant="h6" color="primary">{formatCurrency(financials.cash_flow.free)}</Typography>
                </Box>
              </Card.Content>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <Card.Content>
                <Typography variant="h6" gutterBottom>Customer Metrics</Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Concentration</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress variant="determinate" value={financials.customer_metrics.concentration || 0} color="warning" />
                    </Box>
                    <Typography variant="body2">{formatPercentage(financials.customer_metrics.concentration)}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">Churn</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress variant="determinate" value={financials.customer_metrics.churn_rate || 0} color="error" />
                    </Box>
                    <Typography variant="body2">{formatPercentage(financials.customer_metrics.churn_rate)}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">Retention</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress variant="determinate" value={financials.customer_metrics.retention_rate || 0} color="success" />
                    </Box>
                    <Typography variant="body2">{formatPercentage(financials.customer_metrics.retention_rate)}</Typography>
                  </Box>
                </Box>
              </Card.Content>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
    );
  };

  const renderBalanceSheetTab = () => {
    if (isProcessing) return renderProcessingOverlay();
    return (
      <Box>
        {!financials ? (
          renderNoFinancialData('No Balance Sheet Data Available')
        ) : (
          <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <Card.Content>
                <Typography variant="h6" gutterBottom>Balance Sheet</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Assets</Typography>
                  <Typography>{formatCurrency(financials.balance_sheet.assets)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Liabilities</Typography>
                  <Typography>{formatCurrency(financials.balance_sheet.liabilities)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Equity</Typography>
                  <Typography>{formatCurrency(financials.balance_sheet.equity)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Working Capital</Typography>
                  <Typography variant="h6" color="primary">{formatCurrency(financials.balance_sheet.working_capital)}</Typography>
                </Box>
              </Card.Content>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <Card.Content>
                <Typography variant="h6" gutterBottom>Key Personnel</Typography>
                {financials.key_personnel?.map((person, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ mr: 2 }}><PersonIcon /></Avatar>
                    <Box>
                      <Typography variant="body1">{person.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{person.role}</Typography>
                    </Box>
                  </Box>
                ))}
              </Card.Content>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
    );
  };

  const renderKPIsTab = () => {
    if (isProcessing) return renderProcessingOverlay();
    return (
      <Box>
        {!financials ? (
          renderNoFinancialData('No KPI Data Available')
        ) : (
          <Grid container spacing={3}>
          {Object.entries(financials.kpis).map(([key, value]) => {
            let formattedValue;
            if (value === null || value === undefined || String(value).toLowerCase() === 'none' || String(value).toLowerCase() === 'na') {
              formattedValue = 'N/A';
            } else if (typeof value === 'number') {
              if (key.includes('percentage') || key.includes('margin')) {
                formattedValue = formatPercentage(value);
              } else if (key.includes('ratio')) {
                formattedValue = `${value.toFixed(1)}x`;
              } else if (key.includes('period')) {
                formattedValue = `${value.toFixed(1)} months`;
              } else {
                formattedValue = formatCurrency(value);
              }
            } else {
              formattedValue = value;
            }

            return (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Card>
                  <Card.Content sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">{key.replace(/_/g, ' ').toUpperCase()}</Typography>
                    <Typography variant="h4">
                      {formattedValue}
                    </Typography>
                  </Card.Content>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
    );
  };

  const renderFlagsTab = () => {
    if (isProcessing) return renderProcessingOverlay();
    return (
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flags.length > 0 ? (
                flags.map((flag) => (
                  <TableRow key={flag.id}>
                    <TableCell>{flag.title}</TableCell>
                    <TableCell>{flag.description}</TableCell>
                    <TableCell>
                      <Chip label={flag.severity} color={getSeverityColor(flag.severity) as any} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={flag.status} color={flag.status === 'resolved' ? 'success' : 'default'} size="small" />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Box sx={{ py: 8 }}>
                      <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        No Flags Detected
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        No risk flags or concerns have been identified for this deal
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderQATab = () => {
    if (isProcessing) return renderProcessingOverlay();
    
    // Separate answered and unanswered questions
    const answeredQuestions = questions.filter(q => q.status === 'answered' && q.answer);
    const unansweredQuestions = questions.filter(q => q.status !== 'answered' || !q.answer);
    
    return (
      <Box>
        {questions.length > 0 ? (
          <Box>
            {/* Summary Stats */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>Q&A Summary</Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">{questions.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Questions</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">{answeredQuestions.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Answered</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main">{unansweredQuestions.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Pending</Typography>
                </Box>
              </Box>
            </Box>

            {/* Answered Questions Section */}
            {answeredQuestions.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                  <CheckCircleIcon />
                  Answered Questions ({answeredQuestions.length})
                </Typography>
                {answeredQuestions.map((q) => (
                  <Card key={q.id} sx={{ mb: 2, border: '1px solid', borderColor: 'success.light', bgcolor: 'success.50' }}>
                    <Card.Content>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', flex: 1 }}>{q.question}</Typography>
                        <Chip label="Answered" color="success" size="small" sx={{ ml: 2 }} />
                      </Box>
                      
                      {/* Answer Section - Enhanced */}
                      <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                          AI-Generated Answer:
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <MarkdownRenderer content={q.answer || ''} />
                        </Box>
                        {q.answered_by && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Generated by AI Assistant {q.answered_at ? `on ${new Date(q.answered_at).toLocaleDateString()}` : ''}
                          </Typography>
                        )}
                      </Box>

                      
                      {/* Tags */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Chip label={q.category} size="small" variant="outlined" />
                        <Chip label={q.priority} color={getPriorityColor(q.priority) as any} size="small" />
                      </Box>
                    </Card.Content>
                  </Card>
                ))}
              </Box>
            )}

            {/* Unanswered Questions Section */}
            {unansweredQuestions.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
                    <PsychologyIcon />
                    Pending Questions ({unansweredQuestions.length})
                  </Typography>
                  {/* Temporarily hidden - Answer All Questions button */}
                  {/* <Button
                    variant="contained"
                    color="primary"
                    startIcon={answeringAll ? <CircularProgress size={20} color="inherit" /> : <PsychologyIcon />}
                    onClick={handleAnswerAllQuestions}
                    disabled={answeringAll || dealDocuments.length === 0}
                  >
                    {answeringAll ? 'Generating Answers...' : 'Answer All Questions'}
                  </Button> */}
                </Box>
                {unansweredQuestions.map((q) => (
                  <Card key={q.id} sx={{ mb: 2, border: '1px solid', borderColor: 'warning.light' }}>
                    <Card.Content>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', flex: 1 }}>{q.question}</Typography>
                        <Chip label={q.status} color={getStatusColor(q.status) as any} size="small" sx={{ ml: 2 }} />
                      </Box>
                      
                      {/* Show rationale if available */}
                      {q.rationale && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                          <Typography variant="caption" color="primary" sx={{ fontWeight: 'medium', display: 'block', mb: 0.5 }}>
                            Rationale:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {q.rationale}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Show sub-questions if available */}
                      {q.sub_questions && q.sub_questions.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium', display: 'block', mb: 1 }}>
                            Follow-up Questions:
                          </Typography>
                          <Box sx={{ pl: 2 }}>
                            {q.sub_questions.map((subQ, index) => (
                              <Typography key={index} variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                                <span style={{ marginRight: '8px', color: '#666' }}>•</span>
                                {subQ}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                      
                      {/* Show expected data sources if available */}
                      {q.expected_data_sources && q.expected_data_sources.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium', display: 'block', mb: 1 }}>
                            Expected Data Sources:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {q.expected_data_sources.map((source, index) => (
                              <Chip key={index} label={source} size="small" variant="outlined" color="info" />
                            ))}
                          </Box>
                        </Box>
                      )}
                      
                      {/* Assignment and due date info */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label={q.category} size="small" variant="outlined" />
                          <Chip label={q.priority} color={getPriorityColor(q.priority) as any} size="small" />
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          {q.assigned_to && (
                            <Typography variant="caption" color="text.secondary">
                              Assigned to: {getUserName(q.assigned_to)}
                            </Typography>
                          )}
                          {q.due_date && (
                            <Typography variant="caption" color="text.secondary">
                              Due: {new Date(q.due_date).toLocaleDateString()}
                            </Typography>
                          )}
                          {/* Temporarily hidden - Answer This Question button */}
                          {/* <Button
                            variant="outlined"
                            size="small"
                            startIcon={answeringQuestion === q.id ? <CircularProgress size={16} /> : <PsychologyIcon />}
                            onClick={() => handleAnswerQuestion(q.id)}
                            disabled={answeringQuestion === q.id || dealDocuments.length === 0}
                          >
                            {answeringQuestion === q.id ? 'Generating...' : 'Answer This Question'}
                          </Button> */}
                        </Box>
                      </Box>
                    </Card.Content>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <PsychologyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Questions Generated
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload a CIM document to automatically generate due diligence questions with AI-powered answers
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<UploadIcon />}
              onClick={handleOpenUploadModal}
            >
              Upload CIM Document
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const renderMetadataTab = () => {
    if (isProcessing) return renderProcessingOverlay();
    
    return (
      <Box>
        <Grid container spacing={3}>
          {/* Scoring Context */}
          {aiScore && (
            <Grid item xs={12}>
              <Card>
                <Card.Content>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PsychologyIcon color="primary" />
                    Scoring Context
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {/* Fund Information */}
                    {aiScore.context?.fund_id && (
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom>
                            Scored Against Fund
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                            {getFundName(aiScore.context.fund_id)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                            Fund ID: {aiScore.context.fund_id}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            {selectedFund === 'auto' ? (
                              <Chip label="Auto-Selected" color="success" size="small" />
                            ) : (
                              <Chip label="Manually Selected" color="info" size="small" />
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityIcon />}
                              onClick={() => handleViewFundThesis(aiScore.context.fund_id)}
                              sx={{ ml: 1 }}
                            >
                              View Fund Thesis
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    
                    {/* Investment Thesis */}
                    {aiScore.context?.fund_thesis_id && (
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom>
                            Investment Thesis
                          </Typography>
                          <Typography variant="body2">
                            <strong>Thesis ID:</strong> {aiScore.context.fund_thesis_id}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    
                    {/* Firm Profile */}
                    {aiScore.context?.firm_profile_id && (
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom>
                            Firm Profile
                          </Typography>
                          <Typography variant="body2">
                            <strong>Profile ID:</strong> {aiScore.context.firm_profile_id}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    
                    {/* Global Thesis */}
                    {aiScore.context?.global_thesis_id && (
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom>
                            Global Thesis
                          </Typography>
                          <Typography variant="body2">
                            <strong>Thesis ID:</strong> {aiScore.context.global_thesis_id}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Card.Content>
              </Card>
            </Grid>
          )}
          
          {/* Scoring Metadata */}
          {aiScore?.metadata && (
            <Grid item xs={12}>
              <Card>
                <Card.Content>
                  <Typography variant="h6" gutterBottom>
                    Scoring Details
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="caption" color="text.secondary">Version</Typography>
                        <Typography variant="h6">{aiScore.metadata.version || 'N/A'}</Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="caption" color="text.secondary">Model Version</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {aiScore.metadata.scoring_model_version || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="caption" color="text.secondary">Scored By</Typography>
                        <Typography variant="body2">
                          {aiScore.metadata.scored_by_name || (aiScore.metadata.scored_by ? getUserName(aiScore.metadata.scored_by) : 'N/A')}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="caption" color="text.secondary">Scored At</Typography>
                        <Typography variant="body2">
                          {aiScore.metadata.scored_at ? new Date(aiScore.metadata.scored_at).toLocaleString() : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Card.Content>
              </Card>
            </Grid>
          )}
          
          {/* Deal Documents */}
          <Grid item xs={12}>
            <Paper>
              <Toolbar>
                <Typography variant="h6" sx={{ flex: '1 1 100%' }}>
                  Deal Documents
                </Typography>
              </Toolbar>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Uploaded</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dealDocuments.length > 0 ? (
                      dealDocuments.map((doc) => (
                        <TableRow
                          hover
                          tabIndex={-1}
                          key={doc.id}
                        >
                          <TableCell>{doc.filename || doc.name}</TableCell>
                          <TableCell>{doc.document_type || 'Unknown'}</TableCell>
                          <TableCell>{doc.file_size ? `${(doc.file_size / 1024 / 1024).toFixed(1)}MB` : 'N/A'}</TableCell>
                          <TableCell>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={doc.processing_status || 'Unknown'}
                              color={
                                doc.processing_status === 'completed' ? 'success' :
                                doc.processing_status === 'processing' ? 'warning' :
                                doc.processing_status === 'failed' ? 'error' : 'default'
                              }
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              No documents uploaded yet
                            </Typography>
                            <Button
                              variant="outlined"
                              startIcon={<UploadIcon />}
                              onClick={handleOpenUploadModal}
                            >
                              Upload Document
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          
          {/* No Metadata Available */}
          {!aiScore && dealDocuments.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" gutterBottom>
                  No Metadata Available
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Score the deal and upload documents to see detailed metadata
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={<PsychologyIcon />}
                    onClick={() => setTabValue(0)}
                  >
                    Score Deal
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={handleOpenUploadModal}
                  >
                    Upload Document
                  </Button>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };


  if (loading) {
    return (
      <Box>
        <Paper sx={{ p: 3 }}>
          {/* Header skeleton */}
          <Box sx={{
            height: 40,
            bgcolor: 'grey.300',
            borderRadius: 1,
            mb: 2,
            animation: 'pulse 1.5s ease-in-out infinite',
            width: '60%'
          }} />
          <Box sx={{
            height: 20,
            bgcolor: 'grey.300',
            borderRadius: 1,
            mb: 3,
            animation: 'pulse 1.5s ease-in-out infinite',
            width: '80%'
          }} />
          
          {/* Tabs skeleton */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {Array.from({ length: 7 }).map((_, index) => (
                <Box key={index} sx={{
                  height: 32,
                  bgcolor: 'grey.300',
                  borderRadius: 1,
                  animation: 'pulse 1.5s ease-in-out infinite',
                  width: '80px'
                }} />
              ))}
            </Box>
          </Box>
          
          {/* Content skeleton */}
          <Grid container spacing={3}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Grid item xs={12} md={6} key={index}>
                <MetricCardSkeleton />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>
    );
  }

  if (!deal) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Deal Not Found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          The deal you are looking for does not exist or you do not have access to it.
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/deal-sourcing')}
        >
          Back to Deal Sourcing
        </Button>
      </Box>
    );
  }

  const renderProcessingOverlay = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
      <CircularProgress sx={{ mb: 2 }} />
      <Typography variant="h6">Processing Document</Typography>
      <Typography variant="body2" color="text.secondary">
        Financials and other AI-generated data will be available shortly.
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            {deal.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {deal.description}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/deal-sourcing')}
          >
            Back to Deals
          </Button>
          {deal.status === DealStatus.ConvertedToProject ? (
            <Button
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => {
                navigate(`/projects/${deal.project_id}`);
              }}
              color="primary"
            >
              View Project Details
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={convertingToProject ? <CircularProgress size={20} /> : <TransformIcon />}
              onClick={handleConvertToProject}
              disabled={convertingToProject}
              color="success"
            >
              {convertingToProject ? 'Converting...' : 'Convert to Project'}
            </Button>
          )}
        </Box>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="AI Scoring" {...a11yProps(0)} />
        <Tab label="Financials" {...a11yProps(1)} />
        <Tab label="Cash Flow" {...a11yProps(2)} />
        <Tab label="Balance Sheet" {...a11yProps(3)} />
        <Tab label="KPIs" {...a11yProps(4)} />
        <Tab label="Flags & Risks" {...a11yProps(5)} />
        <Tab label="Q&A" {...a11yProps(6)} />
        <Tab label="Metadata" {...a11yProps(7)} />
        {deal && (user?.id === deal.created_by || currentOrganization?.organization_role === 'Admin' || currentOrganization?.organization_role === 'Owner') && <Tab label="Sharing" {...a11yProps(8)} />}
      </Tabs>

        <TabPanel value={tabValue} index={0}>{renderScoringTab()}</TabPanel>
        <TabPanel value={tabValue} index={1}>{renderFinancialsTab()}</TabPanel>
        <TabPanel value={tabValue} index={2}>{renderCashFlowTab()}</TabPanel>
        <TabPanel value={tabValue} index={3}>{renderBalanceSheetTab()}</TabPanel>
        <TabPanel value={tabValue} index={4}>{renderKPIsTab()}</TabPanel>
        <TabPanel value={tabValue} index={5}>{renderFlagsTab()}</TabPanel>
        <TabPanel value={tabValue} index={6}>{renderQATab()}</TabPanel>
        <TabPanel value={tabValue} index={7}>{renderMetadataTab()}</TabPanel>
        {deal && (user?.id === deal.created_by || currentOrganization?.organization_role === 'Admin' || currentOrganization?.organization_role === 'Owner') && (
          <TabPanel value={tabValue} index={8}>
            {id ? (
              <SharingTab
                entityId={id}
                sharingApi={{
                  getSharedWithUsers: sharedDealsApi.getSharedWithUsers,
                  share: (params) => sharedDealsApi.shareDeal({ deal_id: params.deal_id || '', shared_with_id: params.shared_with_id }),
                  unshare: sharedDealsApi.unshareDeal,
                }}
                entityName="deal"
              />
            ) : (
              <Alert severity="info">Deal not available for sharing.</Alert>
            )}
          </TabPanel>
        )}
      
      <DocumentUpload
        open={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        dealId={id!}
        onUploadComplete={loadDealDetails}
        onUploadInitiated={handleUploadInitiated}
      />

      {/* Scoring Prompt Dialog - shown after document processing */}
      <Dialog
        open={showScoringPrompt}
        onClose={() => setShowScoringPrompt(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">Document Processing Complete!</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 3 }}>
            Your document has been successfully processed. Financial data, risk flags, and due diligence questions have been extracted.
          </Alert>
          
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            Would you like to score this deal now using AI analysis?
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Fund for Scoring</InputLabel>
            <Select
              value={scoringPromptFund}
              onChange={(e) => setScoringPromptFund(e.target.value)}
              label="Select Fund for Scoring"
            >
              <MenuItem value="auto">
                <Box>
                  <Typography variant="body2" fontWeight="medium">Auto (Recommended)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Score against best matching fund
                  </Typography>
                </Box>
              </MenuItem>
              {funds.map((fund: any) => (
                <MenuItem key={fund.id} value={fund.id}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">{fund.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fund.vintage} • ${fund.total_commitments?.toLocaleString()}M
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowScoringPrompt(false)} variant="outlined">
            Skip for Now
          </Button>
          <Button
            onClick={async () => {
              setShowScoringPrompt(false);
              setSelectedFund(scoringPromptFund);
              setTabValue(0); // Switch to AI Scoring tab
              // Trigger scoring
              await handleScoreDeal();
            }}
            variant="contained"
            startIcon={<PsychologyIcon />}
          >
            Score This Deal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fund Thesis Modal */}
      <Dialog
        open={fundThesisModalOpen}
        onClose={handleCloseFundThesisModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {selectedFundThesis?.fund_name} - Investment Thesis
          </Typography>
          <IconButton onClick={handleCloseFundThesisModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedFundThesis && (
            <Box>
              {/* Fund Overview */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Fund Overview
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Vintage</Typography>
                    <Typography variant="body1">{selectedFundThesis.vintage || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Total Commitments</Typography>
                    <Typography variant="body1">
                      {selectedFundThesis.total_commitments ? `$${selectedFundThesis.total_commitments.toLocaleString()}M` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip
                      label={selectedFundThesis.status || 'N/A'}
                      color={selectedFundThesis.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Risk Appetite</Typography>
                    <Typography variant="body1">{selectedFundThesis.risk_appetite || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Investment Thesis */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Investment Thesis
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedFundThesis.description}
                </Typography>
              </Box>

              {/* Core Investment Themes */}
              {selectedFundThesis.core_investment_themes && selectedFundThesis.core_investment_themes.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                    Core Investment Themes
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedFundThesis.core_investment_themes.map((theme: string, index: number) => (
                      <Chip key={index} label={theme} variant="outlined" color="primary" size="small" />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Sector Focus */}
              {selectedFundThesis.sector_focus && selectedFundThesis.sector_focus.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                    Sector Focus
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedFundThesis.sector_focus.map((sector: string, index: number) => (
                      <Chip key={index} label={sector} variant="outlined" color="info" size="small" />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Value Creation Levers */}
              {selectedFundThesis.value_creation_levers && selectedFundThesis.value_creation_levers.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                    Value Creation Levers
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {selectedFundThesis.value_creation_levers.map((lever: string, index: number) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        • {lever}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Deal Breakers */}
              {selectedFundThesis.deal_breakers && selectedFundThesis.deal_breakers.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="medium" color="error">
                    Deal Breakers
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {selectedFundThesis.deal_breakers.map((breaker: string, index: number) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5, color: 'error.main' }}>
                        ⚠ {breaker}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFundThesisModal} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DealSourcingDetail;
